import { cn } from '@/utils/styles';
import { Skeleton } from '@design-system/Skeleton';
import { HomeJourneyCard } from '@screens/HomePage/components/HomeJourneyCard';
import { useGetJourneyStateV2 } from '@viome/viome-base/src/usecase/Home/HomeV2Query';

interface HomeActivateJourneyProps {
  screen: 'nornr' | 'retest';
  screenType?: string;
}

interface JourneyStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
}

export const HomeActivateJourney = ({
  screen,
  screenType = 'nornr',
}: HomeActivateJourneyProps) => {
  const { data: journeyStateData, isLoading, isError } = useGetJourneyStateV2();
  const isRetestScreen = screen === 'retest';

  const isSamplesCollected = journeyStateData?.samples_collected ?? false;

  const steps: JourneyStep[] = [
    {
      id: '1',
      label: 'Register Kit',
      description:
        "Connect your kit to your Viome account, so we know it's you.",
      completed: journeyStateData?.kit_received || false,
    },
    {
      id: '2',
      label: isSamplesCollected
        ? 'Collect Sample(s)'
        : 'Collect & send your sample(s)',
      description:
        'Collect your sample, answer a few quick questions, and ship it to our lab.',
      completed: isSamplesCollected,
    },
    {
      id: '3',
      label: 'Complete Questionnaire',
      description:
        "We'll review important health information and your preferences before releasing your results.",
      completed: journeyStateData?.is_questionaire_completed || false,
    },
  ];

  const allStepsCompleted =
    (journeyStateData?.kit_received ?? false) &&
    isSamplesCollected &&
    (journeyStateData?.is_questionaire_completed ?? false);

  const allSetHeading = `All set! You've completed every step.`;

  const containerHeading =
    screenType === 'nornr'
      ? allStepsCompleted
        ? allSetHeading
        : 'Activate Your Journey'
      : allStepsCompleted
        ? allSetHeading
        : 'Get started with retesting in a few quick steps';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton
          className={cn('h-8 w-48', isRetestScreen && 'rounded-lg bg-white/10')}
        />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              className={cn(
                'h-32 w-full',
                isRetestScreen && 'rounded-lg bg-white/10',
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !journeyStateData) {
    return;
  }

  return (
    <div className={cn('space-y-4', isRetestScreen && 'text-secondary')}>
      <h2
        data-testid="home-activateJourney-heading"
        className={cn(
          'typography-h2 mt-8',
          !isRetestScreen && 'text-primary',
          isRetestScreen && 'text-[#C8CFDBCC]',
        )}
      >
        {containerHeading}
      </h2>
      <div className="grid gap-4">
        {steps.map((step) => (
          <HomeJourneyCard
            key={step.id}
            data-testid={`home-journeyCard-${step.id}`}
            label={step.label}
            description={step.completed ? 'Completed' : step.description}
            completed={step.completed}
            showProgress={step.id === '2' || step.id === '3'}
            progressValue={
              step.id === '2'
                ? journeyStateData?.onboarding_completion_percentage || 0
                : step.id === '3'
                  ? journeyStateData?.questionaire_completion_percentage || 0
                  : 0
            }
            showContinue={!step.completed && (step.id === '2' || step.id === '3')}
            isRetest={isRetestScreen}
            stepId={step.id}
            isQuestionnaireProcessed={journeyStateData?.is_questionaire_processed}
          />
        ))}
      </div>
    </div>
  );
};
